import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  FormControlLabel,
  TextField,
  Stack,
  InputAdornment,
  Dialog,
  DialogContent,
  Radio,
  RadioGroup,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import ProfileTabs from "../common/ProfileTabs";
import { useDispatch, useSelector } from "react-redux";
import { getVideoQuestions, updatePromt } from "../../redux/actions/appActions";
import { ROUTE_RECORDING_TIPS } from "../../constants/routes";
import { useNavigate } from "react-router-dom";
import SeeviHeader from "../common/SeeviHeader";

const StyledTextBox = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  width: "303px",
  borderRadius: "10px",
  padding: "8px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
  marginLeft: "10px",
});

const CustomTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    border: "1px solid #6352CC",
    boxShadow: "0px 0px 11px 2px #4B44D41A inset",
    paddingRight: "12px", // Ensure space for the text and the icon
  },
  "& .MuiInputBase-input": {
    paddingLeft: "10px", // Adjust padding to ensure text does not overlap with the icon
    paddingRight: "12px", // Space on the right side
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "20.83px",
    color: "#1F2235", // Color for the text
    wordBreak: "break-word",
  },
  "& .MuiInputAdornment-root": {
    marginRight: "8px", // Space between the icon and the text
  },
  // Ensure disabled text is visible
  "&.Mui-disabled .MuiInputBase-input": {
    color: "#1F2235", // Ensure text color remains visible
    opacity: 1, // Prevents it from becoming too transparent
  },
}));

const CustomDialog = styled(Dialog)({
  "& .MuiPaper-root": {
    position: "fixed",
    bottom: 0,
    margin: 0,
    width: "100%",

    backgroundColor: "white",
  },
});

const CustomRadio = styled(Radio)({
  "& .MuiSvgIcon-root": {
    fontSize: 20,
  },
  "&.Mui-checked": {
    color: "#756DE9",
  },
  "&.MuiRadio-colorSecondary": {
    color: "#B7B3EB",
  },
});

const Prompt = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const prompts = useSelector((state: any) => state.app.prompts);
  const videoQuestions = useSelector((state: any) => state.app.videoQuestions);
  const [current, setCurrent] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);
  const [errors, setErrors] = useState<string[]>([]); // State for errors

  useEffect(() => {
    dispatch(getVideoQuestions());
  }, []);

  const handleClickOpen = (label: string) => {
    setCurrent(label);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleRadioChange = (event: any) => {
    setSelectedValue(event.target.value);
  };

  const handleSelect = () => {
    const data = videoQuestions
      ?.filter((el: any) => el?.topic === current)[0]
      ?.questions?.filter((x: any) => x?.questionId === parseInt(selectedValue, 10))[0];
    dispatch(updatePromt({ [current]: data }));
    setCurrent("");
    setOpen(false);
  };

  const handleNext = () => {
    // Reset errors
    setErrors([]);
    const newErrors: string[] = [];

    // Check if all prompts have been selected
    videoQuestions.forEach((el: any) => {
      if (!prompts[el?.topic]) {
        newErrors.push(`${el?.topic}`);
      }
    });

    if (newErrors.length > 0) {
      setErrors(newErrors); // Set errors if any
    } else {
      navigate(ROUTE_RECORDING_TIPS); // Proceed to the next step
    }
  };

  return (
    <Box display="" flexDirection="column" justifyContent="center" bgcolor="#eeeeee" width="100%">
      <SeeviHeader />

      <ProfileTabs />

      <Box display="flex" flexDirection="column" alignItems="left" fontSize="14px" fontWeight="500" color="#757575">
        <Box display="flex" alignItems="center" sx={{ backgroundColor: "#eeeeee", padding: "5px 16px" }}>
          <Avatar src="/images/avatar-one.png" alt="Avatar" sx={{ width: "51px", height: "51px" }} />
          <StyledTextBox px={1} mt={2} mb={2}>
            <Box px={1}>
              <Typography className="custom-typography-video">1/3</Typography>
              <Typography className="custom-typography-video">
                Select from a range of interesting prompts within each category
              </Typography>
            </Box>
          </StyledTextBox>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        p={2}
        bgcolor="#ffffff"
        borderRadius="25px 25px 0 0"
        justifyContent="center"
        alignItems="center"
      >
        <Grid container spacing={2} mt={2} paddingLeft="15px">
          {videoQuestions?.map((el: any, index: any) => {
            return (
              <React.Fragment key={index}>
                <Stack spacing={1} mb={2} sx={{ width: "100%" }}>
                  <Typography
                    sx={{ color: "#FD794B", fontSize: "1.25rem", fontWeight: "bold" }}
                    className="custom-typography"
                  >
                    {el?.topic}
                  </Typography>
                  {!prompts[el?.topic] ? (
                    <>
                      <Typography className="custom-typography">Select a Prompt</Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleClickOpen(el?.topic)}
                        sx={{
                          width: "100%", // Full width
                          border: "1px dotted #756DE9", // Dotted border with specified color
                          backgroundColor: "#ffffff", // Background color
                          boxShadow: "none",
                          borderRadius: "100px",
                          color: "#756DE9", // Text color
                          padding: "12px",
                          "&:hover": {
                            backgroundColor: "#f5f5f5", // Background color on hover
                          },
                        }}
                      >
                        Select
                      </Button>
                    </>
                  ) : (
                    <Box onClick={() => handleClickOpen(el?.topic)} style={{ cursor: "pointer" }}>
                      <CustomTextField
                        inputProps={{
                          readOnly: true, // Makes the field non-editable without disabling it
                        }}
                        variant="outlined"
                        multiline
                        // rows={1} // Adjust the number of rows as needed
                        placeholder={prompts?.[el?.topic]["question"]}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <img
                                src="/images/tick-new.png" // Replace with the path to your image
                                alt="Custom"
                                style={{ width: "24px", height: "24px" }} // Adjust size and margin as needed
                              />
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />
                    </Box>
                  )}
                </Stack>
                {index !== videoQuestions?.length - 1 ? (
                  <div style={{ width: "100%" }}>
                    <hr
                      style={{
                        border: "1px solid #c4c4c4",
                        width: "100%",
                        margin: "12px 0", // Adjust margins as needed
                      }}
                    />
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}

          <Stack spacing={1} mb={2} sx={{ width: "100%" }}>
            <CustomDialog open={open} onClose={handleClose}>
              <DialogContent>
                <Box display="flex" flexDirection="column">
                  <Stack spacing={1} mb={5}>
                    {videoQuestions?.map((el: any, index: number) => {
                      return (
                        <Typography
                          key={index}
                          sx={{ color: "#FD794B", fontSize: "1.25rem", fontWeight: "bold" }}
                          className="custom-typography"
                        >
                          {current === el?.topic ? el?.topic : ""}
                        </Typography>
                      );
                    })}

                    <Typography className="custom-typography">Select a prompt for the video.</Typography>
                  </Stack>
                  <RadioGroup value={selectedValue} onChange={handleRadioChange}>
                    {videoQuestions
                      ?.filter((el: any) => el?.topic === current)[0]
                      ?.questions.map((el: any, index: any) => (
                        <React.Fragment key={index}>
                          <Box display="flex" alignItems="flex-start" width="100%">
                            <FormControlLabel
                              value={el?.questionId}
                              control={<CustomRadio />}
                              label={
                                <Typography
                                  sx={{ fontSize: "14px", color: "#1F2235", fontWeight: 400, lineHeight: "18.23px" }}
                                >
                                  {el?.question}
                                </Typography>
                              }
                              sx={{ margin: 0 }}
                            />
                          </Box>
                          {index <
                            videoQuestions?.filter((el: any) => el?.topic === current)[0]?.questions.length - 1 && ( // Adjust the condition to place the divider as needed
                            <div style={{ width: "100%" }}>
                              <hr
                                style={{
                                  border: "1px solid #c4c4c4",
                                  width: "100%",
                                  margin: "12px 0", // Adjust margins as needed
                                }}
                              />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                  </RadioGroup>
                  <Box
                    display="flex"
                    flexDirection="column"
                    marginTop="20px"
                    bgcolor="#ffffff"
                    sx={{ borderRadius: "20px 20px 0px 0px" }}
                  >
                    <Box display="flex">
                      <Button
                        onClick={handleClose}
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "99px",
                          fontSize: "16px",
                          textTransform: "capitalize",
                          width: "25%",
                          color: "#000000",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                        variant="contained"
                        size="small"
                        style={{ padding: "15px 40px" }}
                        className="w-100 mt-2 btn-pad"
                      >
                        Back
                      </Button>
                      <Box flexGrow={1}></Box>
                      <Button
                        sx={{
                          backgroundColor: "#756DE9",
                          borderRadius: "99px",
                          fontSize: "16px",
                          textTransform: "capitalize",
                          width: "70%",
                          paddingY: "15px",
                          paddingX: 0,
                          "&:hover": {
                            backgroundColor: "#756DE9",
                          },
                        }}
                        variant="contained"
                        size="small"
                        className="w-100 mt-2 btn-pad"
                        onClick={handleSelect}
                      >
                        Select Question
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </DialogContent>
            </CustomDialog>
          </Stack>
        </Grid>

        <Box display="flex" justifyContent="center" alignItems="center" mt={4}></Box>
      </Box>
      {errors.length > 0 && (
        <Box sx={{ color: "red", mb: 2, p: 2 }}>
          <Typography sx={{ fontSize: "12px" }}>Please select prompt for {errors?.join(", ")}</Typography>
        </Box>
      )}
      <Box display="flex" flexDirection="column" p={2} bgcolor="#ffffff">
        <Box display="flex">
          <Button
            sx={{
              backgroundColor: "white",
              borderRadius: "99px",
              fontSize: "16px",
              textTransform: "capitalize",
              width: "25%",
              color: "#000000",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
            variant="contained"
            size="small"
            style={{ padding: "15px 40px" }}
            className="w-100 mt-2 btn-pad"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Box flexGrow={1}></Box>
          <Button
            sx={{
              backgroundColor: "#756DE9",
              borderRadius: "99px",
              fontSize: "16px",
              textTransform: "capitalize",
              width: "70%",
              paddingY: "15px",
              paddingX: 0,
              "&:hover": {
                backgroundColor: "#756DE9",
              },
            }}
            variant="contained"
            size="small"
            className="w-100 mt-2 btn-pad"
            onClick={handleNext}
            // onClick={() => navigate(ROUTE_RECORDING_TIPS)}
            disabled={ajaxCallsInProgress}
          >
            Next Up: Tips
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Prompt;
